import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { LOGOS, getLogoForTheme } from '@/config/brand';
import { useTheme } from '@/context/ThemeContext';
import ThemeToggle from './ThemeToggle';

/**
 * PublicNav — fixed top navigation for public pages.
 *
 * Props:
 *  - links: [{ href, label, testId }]
 *  - primaryCta: { href, label, testId }
 *  - secondaryLink: { href, label, testId }  (e.g. "Merchant Login")
 *  - showThemeToggle: bool (default true). Hidden on acq-variant pages.
 *  - forceLogo: optional logo src override (useful for acq pages that
 *    need the all-white logo regardless of global theme class).
 */
export default function PublicNav({
  links = [],
  primaryCta,
  secondaryLink,
  showThemeToggle = true,
  forceLogo,
}) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const logoSrc = forceLogo || getLogoForTheme(theme) || LOGOS.DARK_BG;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/70 backdrop-blur-xl border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="public-nav-logo" className="shrink-0">
          <img src={logoSrc} alt="Hungry Canadian" className="h-8 sm:h-9" />
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}

          {(showThemeToggle || secondaryLink || primaryCta) && links.length > 0 && (
            <div className="h-5 w-px bg-border mx-1" aria-hidden />
          )}

          {showThemeToggle && <ThemeToggle />}

          {secondaryLink && (
            <Link
              to={secondaryLink.href}
              data-testid={secondaryLink.testId || 'public-nav-secondary-link'}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {secondaryLink.label}
            </Link>
          )}

          {primaryCta && (
            <Link
              to={primaryCta.href}
              data-testid={primaryCta.testId || 'public-nav-primary-cta'}
              className="inline-flex items-center h-10 px-5 rounded-full bg-primary hover:bg-primary-hover text-primary-foreground text-sm font-semibold transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
            >
              {primaryCta.label}
            </Link>
          )}
        </div>

        {/* Mobile controls */}
        <div className="flex md:hidden items-center gap-2">
          {showThemeToggle && <ThemeToggle />}

          {/* When only a secondaryLink is provided (e.g. auth pages), show it
              inline on mobile instead of hiding it behind a hamburger drawer. */}
          {!(links.length > 0 || primaryCta) && secondaryLink && (
            <Link
              to={secondaryLink.href}
              data-testid={(secondaryLink.testId || 'public-nav-secondary-link') + '-mobile-inline'}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {secondaryLink.label}
            </Link>
          )}

          {(links.length > 0 || primaryCta) && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="public-nav-mobile-toggle"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border bg-card text-muted-foreground hover:text-foreground transition-colors"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden border-t border-border bg-background"
          data-testid="public-nav-mobile-drawer"
        >
          <div className="px-4 py-4 space-y-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={l.testId ? `${l.testId}-mobile` : undefined}
                className="block px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
              >
                {l.label}
              </a>
            ))}
            {secondaryLink && (
              <Link
                to={secondaryLink.href}
                onClick={() => setOpen(false)}
                data-testid={(secondaryLink.testId || 'public-nav-secondary-link') + '-mobile'}
                className="block px-3 py-3 rounded-lg text-base text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
              >
                {secondaryLink.label}
              </Link>
            )}
            {primaryCta && (
              <Link
                to={primaryCta.href}
                onClick={() => setOpen(false)}
                data-testid={(primaryCta.testId || 'public-nav-primary-cta') + '-mobile'}
                className="block mt-2 text-center px-3 py-3 rounded-full bg-primary hover:bg-primary-hover text-primary-foreground text-base font-semibold transition-colors"
              >
                {primaryCta.label}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
